import { useContext, useEffect, useState } from "react";
import {
  Box, MenuItem, FormControl, Grid, IconButton, InputLabel, Paper, Select,
  Stack, Table, TableBody, TableCell, Container, TableContainer, TableHead,
  TableRow, TextField, styled, tableCellClasses, Pagination, InputBase,
  InputAdornment, Typography, Divider, Backdrop, CircularProgress, Accordion,
  AccordionSummary, AccordionDetails
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { DataRef } from "../../../../types/DataRef";
import { AlertType, SortType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { tableStyle } from "../Pays/Pays";
import CoutRow from "./CoutRow";
import AddCoutForm from "../../../forms/Cout/AddCoutForm";
import TabSort from "../../../element/sort/TabSort";
import { SortParams } from "../../production/SaisieAgent";
import OptionalWidthModal from "../../../../commons/optional-width-modal/OptionalWidthModal";
import { loggoutUser } from "../../../../redux/actions/logginUser";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CheckAttr from "../../../../utils/CheckAttr";
import { useMediaQuery } from "react-responsive";
import { MediaQueryContext } from "../../../../utils/contextes/MediaQueryContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    textAlign: "center",
    fontSize: 11,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    textAlign: "center",
    color: "#fff",
  },
}));

const tableColumnTittles = [
  {
    id: 1,
    tittle: "Pays de production",
    property: "pays.libelle",
    toSort: true,
  },
  {
    id: 2,
    tittle: "Bu",
    property: "bus.libelle",
    toSort: true,
  },
  {
    id: 3,
    tittle: "Client",
    property: "clients.libelle",
    toSort: true,
  },
  {
    id: 4,
    tittle: "Operation",
    property: "operations.libelle",
    toSort: true,
  },
  {
    id: 5,
    tittle: "Société de facturation",
    property: "societe_facturations.libelle",
    toSort: true,
  },
  {
    id: 6,
    tittle: "Code gesco",
    property: "code_gescos.libelleCodeGesco",
    toSort: true,
  },
  {
    id: 7,
    tittle: "Code article",
    property: "code_articles.libelleCodeArticle",
    toSort: true,
  },
  {
    id: 8,
    tittle: "Désignation article",
    property: "articles.libelle",
    toSort: true,
  },
  {
    id: 9,
    tittle: "Coût",
    property: "couts.coutactivite",
    toSort: true,
  },
  {
    id: 10,
    tittle: "Date debut",
    property: "couts.date_debut",
    toSort: true,
  },
  {
    id: 11,
    tittle: "Date fin",
    property: "couts.date_fin",
    toSort: true,
  },
  {
    id: 12,
    tittle: "Devise",
    property: "devises.monnaie",
    toSort: true,
  },
  {
    id: 13,
    tittle: "Actions",
    toSort: false,
  },
];

export interface DataCout {
  pays_id: number;
  pays_libelle: string;
  bu_id: number;
  bu_libelle: string;
  client_id: number;
  client_libelle: string;
  operation_id: number;
  operation_libelle: string;
  socifact_id: number;
  socifact_libelle: string;
  code_gesco_id: number;
  code_gesco_libelle: string;
  code_article_id: number;
  code_article_libelle: string;
  article_id: number;
  article_libelle: string;
  article_actif: number;
  article_desactive_date: Date;
  id?: number;
  cout: string;
  date_debut: Date;
  date_fin: Date;
  devise_id: number;
  devise_libelle: string;
  hasSaisie: boolean;
  couts_parent_id?: number;
}

interface FilterParams {
  societe_facturation: string;
  code_gesco: string;
  operation: string;
  date_debut: string;
}

const initialFilter: FilterParams = {
  societe_facturation: "",
  code_gesco: "",
  operation: "",
  date_debut: "",
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const Cout = () => {
  const { isDesktopOrLaptop } = useContext(MediaQueryContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openSnack, setOpenSnack] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<number | false>(false);

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [totalData, setTotalData] = useState<number>(0);
  const [idCoutsToHaveHours, setIdCoutsToHaveHours] = useState<number[]>([]);
  const [filter, setFilter] = useState<FilterParams>(initialFilter);
  const [sortedColumn, setSortedColumn] = useState<number>(0);
  const [columnToSort, setColumnToSort] = useState<SortParams | undefined>(
    undefined
  );

  const [articleSearch, setArticleSearch] = useState<string>("");
  const [alertMessage, setAlerteMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<AlertType>("success");

  const [dataPays, setDataPays] = useState<DataRef[]>([]);
  const [dataPaysInAssociations, setDataPaysInAssociations] = useState<
    DataRef[]
  >([]);
  const [dataAllClients, setDataAllClients] = useState<DataRef[]>([]);

  const [societeFactFilter, setSocieteFactFilter] = useState<DataRef[]>([]);
  const [codeGescoFilter, setCodeGescoFilter] = useState<DataRef[]>([]);
  const [operationsFilter, setOperationsFilter] = useState<DataRef[]>([]);

  const [dataCouts, setDataCouts] = useState<DataCout[]>([]);

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlBaseApi,
    // timeout: 1000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Add global response interceptor
  http.interceptors.response.use(
    (response) => {
      // Modify response data here, if needed
      return response;
    },
    (error) => {
      console.log(error.message);
      if (error.message === "Request failed with status code 401") {
        // Remove the redux store
        dispatch(loggoutUser());

        // Remove token in navigator cookies
        cookies.remove("jwt", { path: "/" });

        navigate("/sign-in");
      }
      return Promise.reject(error);
    }
  );

  const retrieveData = () => {
    // Récupère les utilisateurs
    setIsLoading(true);

    const data = {
      filter,
      sortby: columnToSort,
      articleSearch,
      rowsPerPage,
      page,
    };

    http
      .post("cout/list", data)
      .then((res) => {
        const { total, data } = res.data;

        setTotalData(total);
        Array.isArray(data) && setDataCouts(data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // Récupère les couts
    retrieveData();
  }, [filter, rowsPerPage, columnToSort, page, refresh]);

  useEffect(() => {
    // Récupère tous les société de facturation qui existe dans l'association dans cout (pour le filtre)
    http
      .get("cout/exist-societe-facturation")
      .then((res) => {
        Array.isArray(res.data) && setSocieteFactFilter(res.data);
      })
      .catch((error) => {
        console.log(error);
      });

    // Récupère tous les pays qui existe dans l'association pays-bu
    http
      .get("pivot-client-bu/get-all-exist-pays-in-association")
      .then((res) => {
        Array.isArray(res.data) && setDataPaysInAssociations(res.data);
      })
      .catch((error) => {
        console.log(error);
      });

    // Récupère les pays
    http
      .get("cout/get-pays")
      .then((res) => {
        Array.isArray(res.data) && setDataPays(res.data);
      })
      .catch((error) => {
        console.log(error);
      });

    // Récupère tous les clients
    http
      .get("client/find-all")
      .then((res) => {
        Array.isArray(res.data) && setDataAllClients(res.data);
      })
      .catch((error) => {
        console.log(error);
      });

    // recuperation des id couts qui ont des heures par periode
    http
      .get("cout/saisie-manager/per-periode")
      .then((res) => {
        const { couts_id } = res.data;

        setIdCoutsToHaveHours(couts_id);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "societe_facturation") {
      setCodeGescoFilter([]);
      setOperationsFilter([]);
      setFilter({
        ...initialFilter,
        societe_facturation: value,
      });
      http
        .get(`cout/exist-code-gesco/via-id-societe-fact/${value}`)
        .then((res) => {
          Array.isArray(res.data) && setCodeGescoFilter(res.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    if (name === "code_gesco") {
      setOperationsFilter([]);
      setFilter({
        ...filter,
        code_gesco: value,
        operation: "",
      });

      http
        .get(
          `cout/exist-operations/via-societe-fact-code-gesco/${filter?.societe_facturation}/${value}`
        )
        .then((res) => {
          Array.isArray(res.data) && setOperationsFilter(res.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    if (name === "date_debut" || name === "operation") {
      setFilter({
        ...filter,
        [name]: value,
      });
    }
  };

  const handleSearch = () => {
    // Recherche par article
    setPage(1);
    setRowsPerPage(5);
    retrieveData();
  };

  const resetFilter = () => {
    setFilter(initialFilter);
    setCodeGescoFilter([]);
    setOperationsFilter([]);
    setColumnToSort(undefined);
    setSortedColumn(0);
    setArticleSearch("");
    reloadList();
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleCloseAlert = () => {
    setOpenSnack(false);
  };

  const setSortParams = (column: string, order: SortType) => {
    setColumnToSort({
      column,
      orderBy: order,
    });
  };

  const iconToUse = (id: number): JSX.Element => {
    let icon = <ArrowUpwardIcon sx={{ fontSize: 13, marginRight: 1 }} />;
    if (sortedColumn === id) {
      if (columnToSort && columnToSort.orderBy === "DESC") {
        icon = <ArrowDownwardIcon sx={{ fontSize: 13, marginRight: 1 }} />;
      }
    }

    return icon;
  };

  const reloadList = () => {
    setRefresh(!refresh);
  };

  const pagination: JSX.Element | boolean = !isLoading && (
    <Stack spacing={2} direction="row">
      <InputLabel sx={{ color: "#fff" }}>Lignes par page :</InputLabel>
      <FormControl variant="standard" sx={{ minWidth: 50 }}>
        <Select
          labelId="demo-simple-select-standard-label"
          id="demo-simple-select-standard"
          color="warning"
          sx={{ background: "#fff", borderRadius: 1, paddingLeft: 1 }}
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(e.target.value as number)}
          defaultValue={5}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>
      {totalData > rowsPerPage && (
        <Pagination
          count={Math.ceil(totalData / rowsPerPage)}
          color="standard"
          sx={{ button: { color: "white" } }}
          showFirstButton={true}
          showLastButton={true}
          siblingCount={0}
          boundaryCount={1}
          defaultPage={page}
          onChange={(event, value) => setPage(value)}
        />
      )}
    </Stack>
  );

  const stackAlert: JSX.Element = SnackBarAlert(
    openSnack,
    handleCloseAlert,
    alertType,
    alertMessage
  );

  return (
    <>
      <Container maxWidth="xl">
        {isDesktopOrLaptop ? (
          <>
            <Grid
              container
              spacing={0.5}
              columns={{ xs: 6, sm: 10, md: 12 }}
            >
              <Grid item xs={6} sm={10} md={3.5} />
              <Grid item xs={2} sm={2} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel
                    id="societe_facturation"
                    color="warning"
                    sx={{ mt: -1 }}
                  >
                    Société de facturation
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="societe_facturation"
                    name="societe_facturation"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Société de facturation"
                    value={filter?.societe_facturation}
                    onChange={handleChange}
                  >
                    {societeFactFilter.map((societe) => (
                      <MenuItem key={societe?.id} value={societe?.id} dense>
                        {societe?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={2} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel id="code_gesco" color="warning" sx={{ mt: -1 }}>
                    Code gesco
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="code_gesco"
                    name="code_gesco"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Code gesco"
                    value={filter?.code_gesco}
                    onChange={handleChange}
                  >
                    {codeGescoFilter.map((code) => (
                      <MenuItem key={code?.id} value={code?.id} dense>
                        {code?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={2} sm={2} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel
                    id="demo-simple-select-label"
                    color="warning"
                    sx={{ mt: -1 }}
                  >
                    Opération
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    name="operation"
                    sx={{ background: "#fff" }}
                    size="small"
                    color="warning"
                    label="Opération"
                    value={filter?.operation}
                    onChange={handleChange}
                  >
                    {operationsFilter.map((operation) => (
                      <MenuItem key={operation?.id} value={operation?.id} dense>
                        {operation?.libelle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={2} sm={2} md={1.5}>
                <TextField
                  name="date_debut"
                  label="Date debut"
                  type="date"
                  color="warning"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 1,
                    mt: { xs: 2, sm: 0 },
                  }}
                  value={filter?.date_debut}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                    style: { color: "#fff", marginTop: -7 },
                  }}
                />
              </Grid>

              <Grid item xs={3} sm={2} md={2}>
                <Stack
                  direction="row"
                  spacing={3}
                  display="flex"
                  ml={3}
                  justifyContent="end"
                  sx={{
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <InputBase
                    sx={{ color: "white" }}
                    value={articleSearch}
                    placeholder="Article ..."
                    onChange={(e) => setArticleSearch(e.target.value)}
                  />
                  <IconButton
                    type="button"
                    sx={{ p: "10px" }}
                    aria-label="search"
                    onClick={() => handleSearch()}
                  >
                    <SearchIcon sx={{ color: "#fff" }} />
                  </IconButton>
                </Stack>
              </Grid>
              <Grid item xs={1} sm={0.5} md={0.5}>
                <IconButton
                  aria-label="refresh"
                  sx={{
                    color: "#fff",
                    pt: 0.5,
                    mt: { xs: 2, sm: 0 },
                  }}
                  onClick={() => resetFilter()}
                >
                  <RefreshIcon sx={{ fontSize: 35 }} />
                </IconButton>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="left" mt={4}>
              {pagination}
            </Box>

            <TableContainer component={Paper} style={tableStyle}>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableColumnTittles.map((column) => (
                      <TabSort
                        key={column.id}
                        column={column}
                        icon={iconToUse(column.id)}
                        sortedColumn={sortedColumn}
                        setSortParams={setSortParams}
                        setSortedColumn={setSortedColumn}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {totalData > 0 ? (
                    dataCouts.map((cout) => (
                      <CoutRow
                        cout={cout}
                        dataPays={dataPays}
                        http={http}
                        isDesktopOrLaptop={isDesktopOrLaptop}
                        idCoutsToHaveHours={idCoutsToHaveHours}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        setOpenSnack={setOpenSnack}
                        setAlerteMessage={setAlerteMessage}
                        setAlertType={setAlertType}
                        reloadList={reloadList}
                      />
                    ))
                  ) : (
                    <StyledTableCell colSpan={13}>
                      Aucun resultat a été trouvé ...
                    </StyledTableCell>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="right" mt={4}>
              {pagination}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{
              position: 'fixed',
              width: '95%',
              top: '10.32rem',
              pr: 1.5,
              pt: 2,
              bgcolor: '#080d24',
              zIndex: 1000
            }}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  Filtre
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel
                      id="demo-simple-select-label"
                      color="warning"
                      sx={{ mt: -1 }}
                    >
                      {!CheckAttr(filter?.societe_facturation) &&
                        "Societe De Facturation"}
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Societe De Facturation"
                      name="societe_facturation"
                      value={filter?.societe_facturation}
                      onChange={handleChange}
                    >
                      <MenuItem value="" dense></MenuItem>
                      {societeFactFilter.map((item) => (
                        <MenuItem key={item?.id} value={item?.id} dense>
                          {item?.libelle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel id="codeGesco" color="warning" sx={{ mt: -1 }}>
                      {!CheckAttr(filter?.code_gesco) && "Code gesco"}
                    </InputLabel>
                    <Select
                      labelId="code_gesco"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Code gesco"
                      name="code_gesco"
                      value={filter?.code_gesco}
                      onChange={handleChange}
                    >
                      <MenuItem value="" dense></MenuItem>
                      {codeGescoFilter.map((item) => (
                        <MenuItem key={item?.id} value={item?.id} dense>
                          {item?.libelle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ mt: 0.1 }} fullWidth>
                    <InputLabel id="operation" color="warning" sx={{ mt: -1 }}>
                      {!CheckAttr(filter?.operation) && "Opération"}
                    </InputLabel>
                    <Select
                      labelId="operation"
                      id="demo-simple-select"
                      size="small"
                      color="warning"
                      label="Opération"
                      name="operation"
                      value={filter?.operation}
                      onChange={handleChange}
                    >
                      <MenuItem value="" dense></MenuItem>
                      {operationsFilter.map((item) => (
                        <MenuItem key={item?.id} value={item?.id} dense>
                          {item?.libelle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      name="date_debut"
                      label="Date debut"
                      type="date"
                      color="warning"
                      sx={{ borderRadius: 1, mt: 2.5 }}
                      size="small"
                      value={filter?.date_debut}
                      onChange={handleChange}
                      InputLabelProps={{
                        shrink: true,
                        style: { color: "#fff", marginTop: -7 },
                      }}
                    />
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              <TextField
                id="outlined-basic"
                variant="outlined"
                sx={{
                  display: { xs: "flex", sm: "none" },
                  bgcolor: "#fff",
                  borderRadius: 1,
                  mt: 1,
                  mb: 3
                }}
                placeholder="Chercher un article ..."
                size="small"
                fullWidth
                value={articleSearch}
                onChange={(e) => setArticleSearch(e.target.value)}
                color="warning"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="search"
                        onClick={() => handleSearch()}
                      >
                        <SearchIcon />
                      </IconButton>
                      <IconButton
                        aria-label="refresh"
                        onClick={() => resetFilter()}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography>Liste des Coûts</Typography>
              <Divider orientation="horizontal" sx={{ bgcolor: "#fff", mb: 2 }} />

              {!isLoading && totalData > rowsPerPage && (
                <Box display="flex" justifyContent="end">
                  <Pagination
                    count={Math.ceil(totalData / rowsPerPage)}
                    size="small"
                    color="standard"
                    sx={{ button: { color: "white" }, mb: 2 }}
                    showFirstButton={true}
                    showLastButton={true}
                    siblingCount={0}
                    boundaryCount={1}
                    defaultPage={page}
                    onChange={(event, value) => setPage(value)}
                  />
                </Box>
              )}
            </Box>

            <Box mt='12.5rem'>
              {dataCouts.map((cout) => (
                <CoutRow
                  cout={cout}
                  dataPays={dataPays}
                  http={http}
                  isDesktopOrLaptop={isDesktopOrLaptop}
                  idCoutsToHaveHours={idCoutsToHaveHours}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  setOpenSnack={setOpenSnack}
                  setAlerteMessage={setAlerteMessage}
                  setAlertType={setAlertType}
                  reloadList={reloadList}
                />
              ))}
            </Box>

            <Stack direction="row" sx={{ mt: 3, mb: 4 }}>
              <InputLabel sx={{ color: "#fff", fontSize: 12, mt: 0.5 }}>
                Lignes par page :
              </InputLabel>
              <FormControl variant="standard" sx={{ minWidth: 48 }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  color="warning"
                  sx={{
                    background: "#fff",
                    borderRadius: 1,
                    paddingLeft: 1,
                    paddingTop: 0.5,
                    marginLeft: 1,
                    fontSize: 12,
                  }}
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(e.target.value as number)}
                  defaultValue={5}
                  size="small"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
        <OptionalWidthModal
          title="Ajouter un coût"
          breackPoint="lg"
          onClose={handleClose}
          open={openModal}
        >
          <AddCoutForm
            dataPaysInAssociations={dataPaysInAssociations}
            dataAllClients={dataAllClients}
            http={http}
            onClose={handleClose}
            setOpenSnack={setOpenSnack}
            setAlerteMessage={setAlerteMessage}
            setAlertType={setAlertType}
            reloadList={reloadList}
          />
        </OptionalWidthModal>
        {stackAlert}
      </Container>

      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </>
  );
};

export default Cout;
